import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ConsentState = 'accepted' | 'declined' | null;

const CONSENT_KEY = 'es_cookie_consent';

export default function CookieConsentBanner() {
  const [consent, setConsent] = useState<ConsentState>(null);
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(true);
  const [adsChecked, setAdsChecked] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Small delay so page renders first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setConsent(stored as ConsentState);
  }, []);

  const applyConsent = (value: ConsentState) => {
    localStorage.setItem(CONSENT_KEY, value as string);
    setConsent(value);
    setVisible(false);

    // Signal Google Analytics / AdSense via gtag consent mode
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const granted = value === 'accepted' ? 'granted' : 'denied';
      (window as any).gtag('consent', 'update', {
        analytics_storage: analyticsChecked && value === 'accepted' ? 'granted' : 'denied',
        ad_storage: adsChecked && value === 'accepted' ? 'granted' : 'denied',
        ad_user_data: adsChecked && value === 'accepted' ? 'granted' : 'denied',
        ad_personalization: adsChecked && value === 'accepted' ? 'granted' : 'denied',
      });
    }
  };

  const handleAcceptAll = () => applyConsent('accepted');

  const handleDecline = () => {
    setAnalyticsChecked(false);
    setAdsChecked(false);
    applyConsent('declined');
  };

  const handleSavePreferences = () => {
    const value: ConsentState = analyticsChecked || adsChecked ? 'accepted' : 'declined';
    applyConsent(value);
  };

  if (!visible || consent !== null) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto bg-gray-950 text-white rounded-2xl overflow-hidden">
        {/* Main banner */}
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Icon */}
            <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
              <i className="ri-shield-check-line text-lg text-amber-400"></i>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold mb-1">We use cookies</h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                We use cookies and similar technologies to personalise content, show relevant ads (via Google AdSense), and analyse traffic. By clicking <strong className="text-white">"Accept All"</strong> you consent to our use of cookies.{' '}
                <Link to="/legal/privacy-policy" className="text-amber-400 underline hover:no-underline">
                  Privacy Policy
                </Link>
              </p>

              {/* Expandable preferences */}
              {showDetails && (
                <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                  {/* Essential — always on */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-white">Essential Cookies</p>
                      <p className="text-xs text-gray-500 mt-0.5">Required for the site to function. Cannot be disabled.</p>
                    </div>
                    <div className="w-10 h-5 bg-teal-500 rounded-full flex items-center justify-end px-0.5 flex-shrink-0 opacity-60 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-white">Analytics Cookies</p>
                      <p className="text-xs text-gray-500 mt-0.5">Help us understand how visitors use the site (Google Analytics).</p>
                    </div>
                    <button
                      onClick={() => setAnalyticsChecked(!analyticsChecked)}
                      className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                        analyticsChecked ? 'bg-teal-500 justify-end' : 'bg-gray-600 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>

                  {/* Advertising */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-white">Advertising Cookies</p>
                      <p className="text-xs text-gray-500 mt-0.5">Used by Google AdSense to show personalised ads based on your interests.</p>
                    </div>
                    <button
                      onClick={() => setAdsChecked(!adsChecked)}
                      className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                        adsChecked ? 'bg-teal-500 justify-end' : 'bg-gray-600 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
              {showDetails ? (
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  Save Preferences
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Manage
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Accept All
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="px-5 sm:px-6 py-2.5 bg-white/5 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-600">
            GDPR &amp; ePrivacy Directive compliant
          </p>
          {showDetails && (
            <button
              onClick={() => setShowDetails(false)}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
            >
              Hide details
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
